import {
  Box,
  BoxProps,
  chakra,
  Flex,
  FlexProps,
  HTMLChakraProps,
  Textarea,
} from '@chakra-ui/react'
import React from 'react'

// Chakra-compatible transition (replaces styled-components easeInOutTransition)
const transitionStyle = 'all 0.15s ease-in-out'

// Semantic tokens from snippetSettingsColors - using strings directly for Chakra v3 compatibility
const BorderColor = 'border.default'
const BackgroundColor = 'bg.default'
const MaxCountColor = 'red.solid'
const MaxCountColor2 = 'fg.muted'
const LoadingColor = 'border.default'

export const RootWrapper = (props: FlexProps) => (
  <Flex
    minWidth="44rem"
    height="100%"
    padding="2.5rem"
    paddingTop="0"
    flexDirection="column"
    backgroundColor="bg.subtle"
    {...props}
  />
)

export const HeaderWrapper = (props: FlexProps) => (
  <Flex
    width="100%"
    padding="0.5rem"
    paddingX="0.5rem"
    paddingTop="1.875rem"
    alignItems="center"
    justifyContent="space-between"
    {...props}
  />
)

export const ErrorWrapper = (props: FlexProps) => {
  return (
    <Flex
      width="100%"
      padding="0.75rem"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={BackgroundColor}
      borderBottom="1px solid"
      borderBottomColor={BorderColor}
      {...props}
    />
  )
}

export const HeaderTitleWrapper = (props: FlexProps) => {
  return (
    <Flex
      alignItems="center"
      css={{
        '> p': {
          marginLeft: '0.5rem',
        },
      }}
      {...props}
    />
  )
}

export const EditTitleWrapper = (props: FlexProps) => {
  return (
    <Flex
      alignItems="center"
      css={{
        '> p': {
          marginRight: '0.25rem',
        },
      }}
      {...props}
    />
  )
}

export const HeaderActionWrapper = (props: FlexProps) => {
  return (
    <Flex
      alignItems="center"
      css={{
        '> div:lastChild': {
          marginLeft: '0.625rem',
        },
      }}
      {...props}
    />
  )
}

export const ListWrapper = (props: BoxProps) => {
  return (
    <Box
      width="100%"
      height="100%"
      overflowY="auto"
      border="1px solid"
      borderColor={BorderColor}
      borderRadius="8px"
      backgroundColor={BackgroundColor}
      {...props}
    />
  )
}

// ItemWrapper is used by Login components with additional props
interface ItemWrapperProps extends Omit<FlexProps, 'bottom'> {
  bottom?: boolean
  active?: boolean
  isSelected?: boolean
}
export const ItemWrapper = ({
  bottom: _bottom,
  active: _active,
  isSelected: _isSelected,
  ...props
}: ItemWrapperProps) => {
  return (
    <Flex
      width="100%"
      height="2.68rem"
      padding="0.5rem 0.75rem"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={BackgroundColor}
      borderBottom="1px solid"
      borderBottomColor={BorderColor}
      cursor="pointer"
      transition={transitionStyle}
      _hover={{ backgroundColor: 'bg.muted' }}
      _active={{ backgroundColor: 'bg.emphasized' }}
      _last={{ borderBottom: 'none' }}
      {...props}
    />
  )
}

export const ItemNameWrapper = (props: FlexProps) => {
  return <Flex width="calc(100% - 60px)" {...props} />
}

export const ItemActionWrapper = (props: FlexProps) => {
  return <Flex alignItems="center" {...props} />
}

export const EditContentWrapper = (props: FlexProps) => {
  return (
    <Flex
      width="100%"
      height="calc(100% - 4.5rem)"
      padding="0.75rem"
      flexDirection="column"
      {...props}
    />
  )
}

export const ShortcutNameInputWrapper = (props: FlexProps) => {
  return <Flex flexDirection="column" width="100%" {...props} />
}

export const GroupDropdownWrapper = (props: FlexProps) => {
  return (
    <Flex
      width="100%"
      display="block"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      borderRadius="0.375rem"
      flexDirection="column"
      {...props}
    />
  )
}

interface EditBodyInputAreaProps {
  hasExceededMaxCount: boolean
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  'aria-labelledby'?: string
}

export const EditBodyInputArea = ({
  hasExceededMaxCount,
  placeholder,
  onChange,
  ...props
}: EditBodyInputAreaProps) => {
  const borderColor = hasExceededMaxCount ? MaxCountColor : MaxCountColor2

  return (
    <Textarea
      width="100%"
      height="100%"
      padding="0.5rem"
      fontSize="13px"
      lineHeight="17px"
      color="fg.default"
      backgroundColor={BackgroundColor}
      borderRadius="0.375rem"
      border="1px solid"
      borderColor={borderColor}
      resize="none"
      verticalAlign="top"
      placeholder={placeholder}
      onChange={onChange}
      {...props}
    />
  )
}

export const EditFooterWrapper = (props: FlexProps) => {
  return (
    <Flex
      width="100%"
      height="3.5rem"
      alignItems="center"
      justifyContent="flex-end"
      borderTop="2px solid"
      borderTopColor={BorderColor}
      paddingRight="0.75rem"
      {...props}
    />
  )
}

export const CancelButtonWrapper = (props: FlexProps) => {
  return <Flex paddingRight="0.25rem" {...props} />
}

export const TextAreaWrapper = (props: FlexProps) => {
  return <Flex position="relative" height="calc(100% - 208px)" {...props} />
}

export const HelperText = (props: FlexProps) => {
  return <Flex margin="0.25rem 0 0.75rem 0" {...props} />
}

export const SnippetLabel = (props: FlexProps) => {
  return <Flex margin="0.25rem 0" {...props} />
}

export const FilterMenuWrapper = (props: FlexProps) => (
  <Flex
    width="100%"
    padding="0.75rem"
    alignItems="center"
    justifyContent="space-between"
    backgroundColor={BackgroundColor}
    borderRadius="8px"
    border="1px solid"
    borderColor={BorderColor}
    cursor="pointer"
    transition={transitionStyle}
    marginY="16px"
    {...props}
  />
)

export const LoadingWrapper = (props: FlexProps) => {
  return <Flex height="100%" {...props} />
}

export const Loading = (props: FlexProps) => {
  return (
    <Flex
      height="100%"
      alignItems="center"
      justifyContent="center"
      backgroundColor={BackgroundColor}
      color={LoadingColor}
      userSelect="none"
      {...props}
    />
  )
}

export const EmptyStateWrapper = (props: FlexProps) => (
  <Flex
    height="100%"
    alignItems="center"
    justifyContent="center"
    backgroundColor={BackgroundColor}
    {...props}
  />
)

interface FilterOptionProps extends HTMLChakraProps<'button'> {
  isSelected: boolean
}
export const FilterOption = ({ isSelected, ...props }: FilterOptionProps) => (
  <chakra.button
    type="button"
    flex={1}
    borderRadius="md"
    p={1}
    bg={isSelected ? 'bg.muted' : 'transparent'}
    cursor="pointer"
    transition="background 200ms ease-in-out"
    _hover={!isSelected ? { bg: 'bg.subtle' } : {}}
    _active={!isSelected ? { bg: 'bg.muted' } : {}}
    textAlign="center"
    lineHeight="1.75"
    color="fg.default"
    _focusVisible={{
      outline: '2px solid',
      outlineColor: 'brand.fg',
      outlineOffset: '2px',
    }}
    {...props}
  />
)
export const FilterOptionWrapper = (props: FlexProps) => (
  <Flex width="100%" gap={2} {...props} />
)
